// src/pages/Login.js
import React, { useMemo, useState } from 'react'
import './Login.css'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

const API_LOGIN = 'https://garibcasinos.com/api/login.php'

const sha256 = async (mensaje) => {
  const encoder = new TextEncoder()
  const data = encoder.encode(mensaje)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// ---------- RUT helpers ----------
const cleanRut = (rut) =>
  (rut || '')
    .toUpperCase()
    .replace(/[^0-9K]/g, '')
    .trim()

const rutDV = (cuerpo) => {
  let suma = 0
  let mul = 2
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += Number(cuerpo[i]) * mul
    mul = mul === 7 ? 2 : mul + 1
  }
  const res = 11 - (suma % 11)
  if (res === 11) return '0'
  if (res === 10) return 'K'
  return String(res)
}

// Formato en vivo: xx.xxx.xxx-x
const formatRutLive = (input) => {
  const r = cleanRut(input)
  if (!r) return ''

  const dv = r.length >= 2 ? r.slice(-1) : ''
  let cuerpo = r.length >= 2 ? r.slice(0, -1) : r

  cuerpo = cuerpo.slice(0, 8)

  const rev = cuerpo.split('').reverse()
  const parts = []
  for (let i = 0; i < rev.length; i += 3) {
    parts.push(rev.slice(i, i + 3).reverse().join(''))
  }
  const cuerpoConPuntos = parts.reverse().join('.')

  return r.length >= 2 ? `${cuerpoConPuntos}-${dv}` : cuerpoConPuntos
}

const isRutValid = (rutFormateado) => {
  const r = cleanRut(rutFormateado)
  if (r.length < 8 || r.length > 10) return false
  const cuerpo = r.slice(0, -1)
  const dv = r.slice(-1)
  if (!/^\d+$/.test(cuerpo)) return false
  return rutDV(cuerpo) === dv
}
// --------------------------------

export default function Login() {
  const [modo, setModo] = useState('admin') // ✅ 'admin' | 'almuerzo'

  // Admin
  const [usuario, setUsuario] = useState('')
  const [clave, setClave] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Almuerzo
  const [rutAlmuerzo, setRutAlmuerzo] = useState('')

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()

  const titulo = useMemo(
    () => (modo === 'admin' ? '🎫 Sistema de Casino' : '🍽️ Elección de Almuerzo'),
    [modo]
  )

  const subtitulo = useMemo(
    () =>
      modo === 'admin'
        ? 'Acceso para administración y reportes.'
        : 'Ingresa tu RUT para programar tus almuerzos del mes.',
    [modo]
  )

  const handleLogin = async (e) => {
    e.preventDefault()
    setError(null)

    // ===== MODO ALMUERZO: solo RUT =====
    if (modo === 'almuerzo') {
      const rutFormateado = formatRutLive(rutAlmuerzo)
      setRutAlmuerzo(rutFormateado)

      if (!isRutValid(rutFormateado)) {
        setError('❌ RUT inválido. Revisa el dígito verificador (DV).')
        return
      }

      localStorage.setItem('rut_almuerzo', rutFormateado)

      setTimeout(() => {
        navigate(`/almuerzoMes?rut=${encodeURIComponent(rutFormateado)}`, {
          replace: true,
          state: { rut: rutFormateado },
        })
      }, 50)
      return
    }

    // ===== MODO ADMIN (y COCINA/AUDITOR): usuario + clave hash =====
    setLoading(true)
    try {
      const claveHash = await sha256(clave)

      const response = await axios.post(
        API_LOGIN,
        { usuario, clave: claveHash },
        { headers: { 'Content-Type': 'application/json' } }
      )

      if (response.data?.success) {
        // ✅ GUARDA auth COMPLETO (rol + maquinas + nombre + correo)
        const auth = {
          usuario: response.data.usuario || usuario,
          nombre: response.data.nombre || '',
          correo: response.data.correo || '',
          rol: response.data.rol || 'COCINA',
          maquinas: Array.isArray(response.data.maquinas) ? response.data.maquinas : [],
          token: response.data.token || '', // por si después agregas token
        }

        localStorage.setItem('auth', JSON.stringify(auth))

        // ✅ legacy opcional (por compatibilidad con partes antiguas)
        localStorage.setItem('usuario', auth.usuario)

        setTimeout(() => navigate('/dashboard', { replace: true }), 50)
      } else {
        setError(response.data?.message || '❌ Credenciales incorrectas')
      }
    } catch (err) {
      console.error('Error de login:', err)
      setError('❗ Error en la conexión con el servidor')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="hero is-fullheight login-hero">
      <div className="hero-body">
        <div className="container" style={{ maxWidth: 480 }}>
          <div className="box login-box">
            <div className="has-text-centered mb-4">
              <h1 className="title mb-1">{titulo}</h1>
              <p className="has-text-grey">{subtitulo}</p>
            </div>

            <div className="tabs is-toggle is-fullwidth mb-4">
              <ul>
                <li className={modo === 'admin' ? 'is-active' : ''}>
                  <a onClick={() => setModo('admin')}>
                    <span className="icon is-small">
                      <i className="fas fa-cog" />
                    </span>
                    <span>Administrador</span>
                  </a>
                </li>
                <li className={modo === 'almuerzo' ? 'is-active' : ''}>
                  <a onClick={() => setModo('almuerzo')}>
                    <span className="icon is-small">
                      <i className="fas fa-utensils" />
                    </span>
                    <span>Almuerzo</span>
                  </a>
                </li>
              </ul>
            </div>

            {error && <div className="notification is-danger is-light">{error}</div>}

            <form onSubmit={handleLogin}>
              {/* ========= ADMIN / COCINA / AUDITOR ========= */}
              {modo === 'admin' && (
                <>
                  <div className="field">
                    <label className="label">Usuario</label>
                    <div className="control has-icons-left">
                      <input
                        className="input"
                        type="text"
                        value={usuario}
                        onChange={(e) => setUsuario(e.target.value)}
                        required
                        placeholder="admin / baika.hijuelas"
                      />
                      <span className="icon is-small is-left">
                        <i className="fas fa-user" />
                      </span>
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Contraseña</label>
                    <div className="control has-icons-left has-icons-right" style={{ position: 'relative' }}>
                      <input
                        className="input"
                        type={showPassword ? 'text' : 'password'}
                        value={clave}
                        onChange={(e) => setClave(e.target.value)}
                        required
                        placeholder="••••••••"
                      />
                      <span className="icon is-small is-left">
                        <i className="fas fa-lock" />
                      </span>

                      <span
                        className="icon is-small is-right"
                        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        onClick={() => setShowPassword((v) => !v)}
                        title={showPassword ? 'Ocultar' : 'Mostrar'}
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* ========= ALMUERZO ========= */}
              {modo === 'almuerzo' && (
                <div className="field">
                  <label className="label">RUT</label>
                  <div className="control has-icons-left">
                    <input
                      className="input"
                      type="text"
                      value={rutAlmuerzo}
                      onChange={(e) => setRutAlmuerzo(formatRutLive(e.target.value))}
                      required
                      placeholder="12.345.678-9"
                      inputMode="text"
                      maxLength={12}
                    />
                    <span className="icon is-small is-left">
                      <i className="fas fa-id-card" />
                    </span>
                  </div>

                  <p className="help">
                    Formato automático: <b>12.345.678-9</b>.
                  </p>
                </div>
              )}

              <div className="field mt-4">
                <button className={`button is-fullwidth ${modo === 'admin' ? 'is-primary' : 'is-link'}`} type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="icon is-small">
                        <i className="fas fa-spinner fa-pulse" />
                      </span>
                      <span>Validando…</span>
                    </>
                  ) : (
                    <>
                      <span className="icon is-small">
                        <i className={`fas ${modo === 'admin' ? 'fa-sign-in-alt' : 'fa-arrow-right'}`} />
                      </span>
                      <span>{modo === 'admin' ? 'Ingresar al Dashboard' : 'Continuar a Elegir Almuerzo'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <p className="has-text-centered has-text-grey-light mt-4" style={{ fontSize: 12 }}>
            SistemaRAB · CasinoApp
          </p>
        </div>
      </div>
    </section>
  )
}
